/// <amd-module name="VSS/Features/Charts/Internal/StringUtilities" />

import { isNumeric } from "./CompatUtilities";

export function htmlEncode(input: string) {
    if (!input) {
        return "";
    }

    let result = input;

    if (typeof input !== "string") {
        result = "" + input;
    }

    return result
        .replace(/&/g, "&amp;")
        .replace(/>/g, "&gt;")
        .replace(/</g, "&lt;");
}

export function decodeHtmlSpecialChars(str: string): string {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = str;
    return textArea.value;
}

/**
 * Encapsulate all our calls in Charts for encoding untrusted strings for traceability.
 *
 * Note: HTML encoding of strings prevents malicious XSS rendering when rendered under SVG.
 * However, not all Encoded characters are pleasantly interpreted for rendering in SVG as literals.
 * This has adverse effect of showing some encoded text as literal in SVG.
 *
 * In future, we will likely want to specialize the render paths, with SVG vs HTML aware call contract.
 * @param val
 */
export function encodeUntrustedString(val: string): string {
    if (typeof val === "string") {
        val = htmlEncode(val);
    }

    return val;
}

export function encodeUntrustedStringArray(names: string[]): string[] {
    return names.map((val: string) => {
        return encodeUntrustedString(val);
    });
}

/**
 * HTML decodes special characters such as < and > in the given string.
 * @param val string to decode.
 * @returns HTML decoded string.
 */
export function decodeString(val: string): string {
    if (typeof val === "string") {
        val = decodeHtmlSpecialChars(val);
    }

    return val;
}

/**
 * Truncate the string if its decoded length exceeds the given length.
 * If truncated an ellipsis is appended.
 * @param value in which to decode whitelisted special characters.
 * @param length to which the string should be truncated.
 * @returns partially decoded truncated string.
 */
export function truncateString(value: string, length: number) {
    // 'value' is assumed to be encoded. We don't want truncation to split encoded characters so we perform the following actions
    // as a best effort to render the string as intended by the user. Unfortunately this means we cannot guarantee
    // truncation length in the case where an encoded character isn't interpreted by SVG.
    if (typeof value === "string") {
        // Truncate only if a truncation length is specified
        if (isNumeric(length)) {
            // 1. Decode
            value = decodeHtmlSpecialChars(value);

            // 2. Truncate
            let text = value;
            if (text.length > length) {
                value = text.substr(0, length) + "...";
            }

            // 3. Encode
            value = encodeUntrustedString(value);
        }
    }

    return value;
}
